import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Box, Typography, Slider } from '@mui/material';
import { ThemeContext } from 'styled-components';

const ParticipantSlider = ({ participants, participantPercentages, onPercentageChange, theme, maxPercentage = 100 }) => {
    const sliderRef = useRef(null);
    const [sliderWidth, setSliderWidth] = useState(0);
    const [activeBoundaryIndex, setActiveBoundaryIndex] = useState(null);
    const activeBoundaryIndexRef = useRef(null);
    const dragStartRef = useRef({ x: 0, position: 0 });
    const dragHandlersRef = useRef({ move: null, up: null });

    useEffect(() => {
        const updateWidth = () => {
            if (sliderRef.current) {
                setSliderWidth(sliderRef.current.offsetWidth);
            }
        };
        
        updateWidth();
        const handleResize = () => updateWidth();
        const timeoutId = setTimeout(updateWidth, 100);
        
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            clearTimeout(timeoutId);
        };
    }, []);

    // Calculate sections and boundaries
    const sections = [];
    const boundaries = [];
    let cumulative = 0;
    
    participants.forEach((participant, index) => {
        const key = participant.id || participant.email;
        const percentage = participantPercentages[key] || 0;
        const startPercent = cumulative;
        const endPercent = cumulative + percentage;
        
        sections.push({
            participant,
            start: startPercent,
            end: endPercent,
            percentage,
        });
        
        if (index < participants.length - 1) {
            boundaries.push({
                index: index,
                position: endPercent,
                leftParticipant: participant,
                rightParticipant: participants[index + 1],
            });
        }
        
        cumulative += percentage;
    });

    const getEmailPrefix = (email) => {
        if (!email) return '???';
        return email.substring(0, 3).toUpperCase();
    };

    // Handle mouse move during drag - defined outside useEffect so it can be called immediately
    const createDragHandlers = useCallback((boundaryIndex) => {
        const handleMouseMove = (e) => {
            if (!sliderRef.current || sliderWidth === 0 || activeBoundaryIndexRef.current !== boundaryIndex) return;
            
            e.preventDefault();
            e.stopPropagation();
            
            const rect = sliderRef.current.getBoundingClientRect();
            const clientX = e.clientX !== undefined ? e.clientX : (e.touches && e.touches[0] ? e.touches[0].clientX : 0);
            const mouseX = clientX - rect.left;
            const deltaX = mouseX - dragStartRef.current.x;
            const deltaPercent = (deltaX / sliderWidth) * 100;
            
            const newPosition = Math.max(0, Math.min(maxPercentage, dragStartRef.current.position + deltaPercent));
            const adjustment = newPosition - boundaries[boundaryIndex].position;
            
            const leftSection = sections[boundaryIndex];
            const rightSection = sections[boundaryIndex + 1];
            
            if (!leftSection || !rightSection) return;
            
            const newLeftPercent = Math.max(0, leftSection.percentage + adjustment);
            const newRightPercent = Math.max(0, rightSection.percentage - adjustment);
            
            const leftKey = leftSection.participant.id || leftSection.participant.email;
            const rightKey = rightSection.participant.id || rightSection.participant.email;
            
            // Update only the two participants involved in the drag
            // Don't scale or adjust other participants
            onPercentageChange(leftKey, newLeftPercent);
            onPercentageChange(rightKey, newRightPercent);
        };

        const handleMouseUp = () => {
            // Clean up event listeners
            if (dragHandlersRef.current.move) {
                document.removeEventListener('mousemove', dragHandlersRef.current.move, { capture: true });
                document.removeEventListener('pointermove', dragHandlersRef.current.move, { capture: true });
            }
            if (dragHandlersRef.current.up) {
                document.removeEventListener('mouseup', dragHandlersRef.current.up, { capture: true });
                document.removeEventListener('pointerup', dragHandlersRef.current.up, { capture: true });
                document.removeEventListener('pointercancel', dragHandlersRef.current.up, { capture: true });
            }
            dragHandlersRef.current = { move: null, up: null };
            activeBoundaryIndexRef.current = null;
            setActiveBoundaryIndex(null);
        };

        return { handleMouseMove, handleMouseUp };
    }, [sliderWidth, maxPercentage, participants, participantPercentages, onPercentageChange, boundaries, sections]);

    // Handle mouse down - start drag
    const handleBoundaryMouseDown = useCallback((boundaryIndex, e) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (!sliderRef.current || sliderWidth === 0) return;
        
        const boundary = boundaries[boundaryIndex];
        if (!boundary) return;
        
        // Clean up any existing handlers
        if (dragHandlersRef.current.move) {
            document.removeEventListener('mousemove', dragHandlersRef.current.move, { capture: true });
            document.removeEventListener('pointermove', dragHandlersRef.current.move, { capture: true });
        }
        if (dragHandlersRef.current.up) {
            document.removeEventListener('mouseup', dragHandlersRef.current.up, { capture: true });
            document.removeEventListener('pointerup', dragHandlersRef.current.up, { capture: true });
            document.removeEventListener('pointercancel', dragHandlersRef.current.up, { capture: true });
        }
        
        // Immediately set active state (both state and ref)
        activeBoundaryIndexRef.current = boundaryIndex;
        setActiveBoundaryIndex(boundaryIndex);
        
        const rect = sliderRef.current.getBoundingClientRect();
        const clientX = e.clientX !== undefined ? e.clientX : (e.touches && e.touches[0] ? e.touches[0].clientX : e.clientX);
        const mouseX = clientX - rect.left;
        
        dragStartRef.current = {
            x: mouseX,
            position: boundary.position
        };
        
        // Create and attach handlers immediately
        const { handleMouseMove, handleMouseUp } = createDragHandlers(boundaryIndex);
        dragHandlersRef.current = {
            move: handleMouseMove,
            up: handleMouseUp
        };
        
        // Attach listeners immediately
        document.addEventListener('mousemove', handleMouseMove, { capture: true, passive: false });
        document.addEventListener('pointermove', handleMouseMove, { capture: true, passive: false });
        document.addEventListener('mouseup', handleMouseUp, { capture: true, passive: false });
        document.addEventListener('pointerup', handleMouseUp, { capture: true, passive: false });
        document.addEventListener('pointercancel', handleMouseUp, { capture: true, passive: false });
    }, [sliderWidth, boundaries, createDragHandlers]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (dragHandlersRef.current.move) {
                document.removeEventListener('mousemove', dragHandlersRef.current.move, { capture: true });
                document.removeEventListener('pointermove', dragHandlersRef.current.move, { capture: true });
            }
            if (dragHandlersRef.current.up) {
                document.removeEventListener('mouseup', dragHandlersRef.current.up, { capture: true });
                document.removeEventListener('pointerup', dragHandlersRef.current.up, { capture: true });
                document.removeEventListener('pointercancel', dragHandlersRef.current.up, { capture: true });
            }
        };
    }, []);

    return (
        <Box 
            sx={{ 
                position: 'relative', 
                width: '100%', 
                overflow: 'visible',
                userSelect: 'none',
            }}
        >
            {/* Slider track */}
            <Box
                ref={sliderRef}
                sx={{
                    position: 'relative',
                    width: '100%',
                    height: 40,
                    backgroundColor: 'transparent',
                    borderRadius: 4,
                    overflow: 'hidden',
                    border: `1px solid ${theme.colors.text}`,
                }}
            >
                {/* Colored sections */}
                {sections.map((section) => {
                    const emailPrefix = getEmailPrefix(section.participant.email);
                    const sectionKey = section.participant.id || section.participant.email;
                    
                    return (
                        <Box
                            key={sectionKey}
                            sx={{
                                position: 'absolute',
                                left: `${section.start}%`,
                                width: `${section.end - section.start}%`,
                                height: '100%',
                                backgroundColor: theme.colors.brandSecondary,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                opacity: 0.7,
                            }}
                        >
                            <Typography
                                variant="caption"
                                sx={{
                                    fontSize: '11px',
                                    fontWeight: 600,
                                    color: theme.colors.text,
                                    textAlign: 'center',
                                }}
                            >
                                {emailPrefix}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>

            {/* Divider at end of active portion */}
            {(() => {
                const totalActivePercent = participants.reduce((sum, p) => {
                    const key = p.id || p.email;
                    return sum + (participantPercentages[key] || 0);
                }, 0);
                
                if (totalActivePercent < maxPercentage) {
                    return (
                        <Box
                            sx={{
                                position: 'absolute',
                                left: `${totalActivePercent}%`,
                                top: 0,
                                width: '1px',
                                height: '100%',
                                backgroundColor: theme.colors.text,
                            }}
                        />
                    );
                }
                return null;
            })()}

            {/* Boundary dots */}
            {boundaries.map((boundary, index) => {
                const isActive = activeBoundaryIndex === index;
                return (
                    <Box
                        key={`boundary-${index}`}
                        component="div"
                        role="slider"
                        aria-valuenow={boundary.position}
                        aria-valuemin={0}
                        aria-valuemax={maxPercentage}
                        tabIndex={0}
                        sx={{
                            position: 'absolute',
                            left: `calc(${boundary.position}% - 12px)`,
                            top: '-12px',
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            backgroundColor: theme.colors.text,
                            border: `2px solid ${theme.colors.primary || theme.colors.backgroundPrimary}`,
                            cursor: isActive ? 'grabbing' : 'grab',
                            zIndex: isActive ? 10 : 2,
                            userSelect: 'none',
                            touchAction: 'none',
                            pointerEvents: 'auto',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            outline: 'none',
                            '&:hover': {
                                transform: isActive ? 'scale(1.2)' : 'scale(1.1)',
                                zIndex: isActive ? 10 : 3,
                            },
                            '&:focus-visible': {
                                outline: `2px solid ${theme.colors.secondary}`,
                                outlineOffset: 2,
                            },
                        }}
                        onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleBoundaryMouseDown(index, e);
                        }}
                        onPointerDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleBoundaryMouseDown(index, e);
                        }}
                        draggable={false}
                        title={`Drag to adjust: ${boundary.leftParticipant.email?.substring(0, 10)}... / ${boundary.rightParticipant.email?.substring(0, 10)}...`}
                    >
                        {/* Inner dot */}
                        <Box
                            sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                backgroundColor: theme.colors.primary || theme.colors.backgroundPrimary,
                            }}
                        />
                        {/* Vertical line extending downward */}
                        <Box
                            sx={{
                                position: 'absolute',
                                left: '50%',
                                top: '24px',
                                transform: 'translateX(-50%)',
                                width: '1px',
                                height: '20px',
                                backgroundColor: theme.colors.text,
                                pointerEvents: 'none',
                            }}
                        />
                    </Box>
                );
            })}
        </Box>
    );
};

export default ParticipantSlider;
